import {
  ANONYMOUS,
  loadTossPayments,
  type TossPaymentsWidgets,
} from '@tosspayments/tosspayments-sdk';

// Toss 결제위젯 클라이언트 키. 배포 시 VITE_TOSS_CLIENT_KEY로 주입한다.
// ⚠️ 위젯 키는 'gck', 서버 시크릿 키는 'gsk'다. 클라/시크릿은 반드시 같은 계정의 '세트'여야
//    결제 승인(confirm)이 성공한다. (세트가 아니면 401 / INVALID_API_KEY)
// 기본값은 Toss 공식 문서의 공개 테스트 키 — 가입 없이 테스트할 수 있다.
export const TOSS_CLIENT_KEY: string =
  import.meta.env.VITE_TOSS_CLIENT_KEY ?? 'test_gck_docs_Ovk5rk1EwkEbP0W43n07xlzm';

/** 결제수단 UI를 렌더할 DOM 요소 id */
export const TOSS_PAYMENT_METHOD_ID = 'toss-payment-method';
/** 약관 UI를 렌더할 DOM 요소 id */
export const TOSS_AGREEMENT_ID = 'toss-agreement';

/**
 * 결제위젯 인스턴스를 만들고 결제 금액을 설정한다.
 * 위젯은 카드·간편결제·계좌이체 등을 한 번에 제공하며, 노출할 수단은 Toss 어드민에서 조정한다.
 */
export async function createTossWidgets(amount: number): Promise<TossPaymentsWidgets> {
  const tossPayments = await loadTossPayments(TOSS_CLIENT_KEY);
  const widgets = tossPayments.widgets({ customerKey: ANONYMOUS });
  // 금액은 렌더 전에 설정해야 위젯이 올바른 금액으로 그려진다.
  await widgets.setAmount({ currency: 'KRW', value: amount });
  return widgets;
}

/** 결제수단·약관 UI를 화면에 렌더한다. */
export async function renderTossWidgets(widgets: TossPaymentsWidgets): Promise<void> {
  await Promise.all([
    widgets.renderPaymentMethods({
      selector: `#${TOSS_PAYMENT_METHOD_ID}`,
      variantKey: 'DEFAULT',
    }),
    widgets.renderAgreement({
      selector: `#${TOSS_AGREEMENT_ID}`,
      variantKey: 'AGREEMENT',
    }),
  ]);
}

interface RequestTossWidgetPaymentParams {
  orderId: string;
  orderName: string;
}

/**
 * 선택한 결제수단으로 결제를 요청한다.
 * 성공 시 `/payment/success?paymentKey=&orderId=&amount=` 로,
 * 실패/취소 시 `/payment/fail?code=&message=&orderId=` 로 이동한다.
 * 서버 승인(confirm) 로직은 결제창 방식과 동일하다.
 */
export async function requestTossWidgetPayment(
  widgets: TossPaymentsWidgets,
  params: RequestTossWidgetPaymentParams,
): Promise<void> {
  const { orderId, orderName } = params;
  await widgets.requestPayment({
    orderId,
    orderName,
    successUrl: `${window.location.origin}/payment/success`,
    failUrl: `${window.location.origin}/payment/fail`,
  });
}
